import {
  CustomSchemaServicePublic,
  FieldmetaPublic,
  GetCustomSchemaConnectionsByPropertyRequest,
  UpdateVarsRequest,
} from '@fullstory/proto/fs/services/fieldmeta';
import { Scope } from '@fullstory/proto/fs/api/vars';
import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import { SearchMetaTypes } from './types';
import { Flags } from '@fullstory/client-api';
import * as dxo from '@fullstory/proto/fs/services/dx-objects';
import { AppDispatch, AppThunkAction } from '../../types';
import {
  getEventConnectionsLoading,
  getCustomEventPropsLoading,
  getPagePropsLoading,
  getPageConnectionsLoading,
  getElementPropsLoading,
  getElementConnectionsLoading,
  getCustomEventPropsLastLoaded,
  getPagePropsLastLoaded,
  getElementPropsLastLoaded,
  getEventConnectionsLastLoaded,
  getPageConnectionsLastLoaded,
  getElementConnectionsLastLoaded,
} from './selectors';
import { unscopedVarName } from '@fullstory/util/vars';
import { createAction, ActionType } from '@fullstory/redux-util/utils/types';
import { CacheKeyPredicate, defaultCacheKey } from '@fullstory/requests';

const MAX_AGE_MS_MINUTE = 60 * 1000;

type VarUpdate = UpdateVarsRequest.VarUpdate;

const invalidateCacheKey: CacheKeyPredicate = (key: string): boolean =>
  key.startsWith(defaultCacheKey(dxo.AppBackendPublic.GetPropertySettingsEndpoint, undefined)) ||
  key.startsWith(defaultCacheKey(dxo.AppBackendPublic.GetPropertyDetailViewEndpoint, undefined));

export const SearchMetaRequestActions = {
  getCustomSchema: (orgId: string, flags: Flags) =>
    createGRPCAction<typeof CustomSchemaServicePublic.GetCustomSchema>()(
      {
        type: SearchMetaTypes.GetCustomSchemaRequest,
        grpcFunction: CustomSchemaServicePublic.GetCustomSchema,
        body: { orgId, forceFresh: false },
        readWrite: 'read',
        maxAge: MAX_AGE_MS_MINUTE, // cheap to request, so cache only for a minute to prefer fresh data
      },
      SearchMetaTypes.GetCustomSchemaSuccess,
      SearchMetaTypes.GetCustomSchemaFailure,
    ),
  getCustomSchemaConnections: (
    orgId: string,
    events: string[],
    pages: number[],
    elements: string[],
  ) =>
    createGRPCAction<typeof CustomSchemaServicePublic.GetCustomSchemaConnections>()(
      {
        type: SearchMetaTypes.GetCustomSchemaConnectionsRequest,
        grpcFunction: CustomSchemaServicePublic.GetCustomSchemaConnections,
        body: { orgId, events, pages, elements, forceFresh: false },
        readWrite: 'read',
        maxAge: MAX_AGE_MS_MINUTE, // cheap to request, so cache only for a minute to prefer fresh data
      },
      SearchMetaTypes.GetCustomSchemaConnectionsSuccess,
      SearchMetaTypes.GetCustomSchemaConnectionsFailure,
    ),
  getCustomSchemaConnectionsByProperty: (
    orgId: string,
    properties: GetCustomSchemaConnectionsByPropertyRequest.Property[],
    scope: Scope,
  ) =>
    createGRPCAction<typeof CustomSchemaServicePublic.GetCustomSchemaConnectionsByProperty>()(
      {
        type: SearchMetaTypes.GetCustomSchemaConnectionsByPropertyRequest,
        grpcFunction: CustomSchemaServicePublic.GetCustomSchemaConnectionsByProperty,
        body: { orgId, properties, scope: scope ?? Scope.INVALID_SCOPE }, // INVALID_SCOPE results in all scopes
        readWrite: 'read',
        maxAge: MAX_AGE_MS_MINUTE, // not as cheap to request, but matching other custom schema cache max ages for consistency
      },
      SearchMetaTypes.GetCustomSchemaConnectionsByPropertySuccess,
      SearchMetaTypes.GetCustomSchemaConnectionsByPropertyFailure,
    ),
  getHiddenVars: (orgId: string) =>
    createGRPCAction<typeof FieldmetaPublic.GetArchivedVars>()(
      {
        type: SearchMetaTypes.GetHiddenVarsRequest,
        grpcFunction: FieldmetaPublic.GetArchivedVars,
        body: { orgId },
        readWrite: 'read',
      },
      SearchMetaTypes.GetHiddenVarsSuccess,
      SearchMetaTypes.GetHiddenVarsFailure,
    ),
  updateVarRule: (orgId: string, rule: VarUpdate) =>
    createGRPCAction<typeof FieldmetaPublic.UpdateVars>()(
      {
        type: SearchMetaTypes.UpdateVarsRequest,
        grpcFunction: FieldmetaPublic.UpdateVars,
        body: { orgId, updates: [{ ...rule }] },
        force: true,
        readWrite: 'write',
      },
      SearchMetaTypes.UpdateVarsSuccess,
      SearchMetaTypes.UpdateVarsFailure,
    ),
  updateVarRules: (orgId: string, updates: VarUpdate[]) =>
    createGRPCAction<typeof FieldmetaPublic.UpdateVars>()(
      {
        type: SearchMetaTypes.UpdateVarsRequest,
        grpcFunction: FieldmetaPublic.UpdateVars,
        body: { orgId, updates },
        force: true,
        invalidateCacheKey,
        readWrite: 'write',
      },
      SearchMetaTypes.UpdateVarsSuccess,
      SearchMetaTypes.UpdateVarsFailure,
    ),
};

export const SearchMetaActions = {
  markConnectionsLoading: (events: string[], pages: number[], elements: string[]) =>
    createAction({
      type: SearchMetaTypes.MarkConnectionsLoading,
      events,
      pages,
      elements,
    }),
  markConnectionsByPropertyLoading: (
    properties: GetCustomSchemaConnectionsByPropertyRequest.Property[],
    scope: Scope,
  ) =>
    createAction({
      type: SearchMetaTypes.MarkConnectionsByPropertyLoading,
      properties,
      scope,
    }),
};

export const SearchMetaAsyncRequestActions = {
  getCustomSchemaConnections:
    (
      orgId: string,
      ids: { events?: string[]; pages?: number[]; elements?: string[] },
    ): SearchMetaThunkAction =>
    (dispatch: AppDispatch, state) => {
      const customEventPropsLoading = getCustomEventPropsLoading(state());
      const customEventPropsLastLoaded = getCustomEventPropsLastLoaded(state());
      const pagePropsLoading = getPagePropsLoading(state());
      const pagePropsLastLoaded = getPagePropsLastLoaded(state());
      const elementPropsLoading = getElementPropsLoading(state());
      const elementPropsLastLoaded = getElementPropsLastLoaded(state());
      // only request connections for those that we don't have yet or haven't requested in a bit
      const now = Date.now();
      const needsRefetch = (lastLoaded: Date | undefined) =>
        now - (lastLoaded?.getTime() ?? 0) >= MAX_AGE_MS_MINUTE;
      const reqEvents =
        ids.events?.filter(
          ev =>
            !customEventPropsLoading.has(ev) && needsRefetch(customEventPropsLastLoaded.get(ev)),
        ) ?? [];
      const reqPages =
        ids.pages?.filter(
          p => !pagePropsLoading.has(p) && needsRefetch(pagePropsLastLoaded.get(p)),
        ) ?? [];
      const reqElements =
        ids.elements?.filter(
          el => !elementPropsLoading.has(el) && needsRefetch(elementPropsLastLoaded.get(el)),
        ) ?? [];
      if (reqEvents.length || reqPages.length || reqElements.length) {
        dispatch(SearchMetaActions.markConnectionsLoading(reqEvents, reqPages, reqElements)); // immediately mark loading (req might get queued)
        dispatch(
          SearchMetaRequestActions.getCustomSchemaConnections(
            orgId,
            reqEvents,
            reqPages,
            reqElements,
          ),
        );
      }
    },
  getCustomSchemaConnectionsByProperty:
    (
      orgId: string,
      properties: GetCustomSchemaConnectionsByPropertyRequest.Property[],
      scope: Scope,
    ): SearchMetaThunkAction =>
    (dispatch: AppDispatch, state) => {
      const eventConnectionsByFieldLoading = getEventConnectionsLoading(state());
      const eventConnectionsByFieldLastLoaded = getEventConnectionsLastLoaded(state());
      const pageConnectionsByFieldLoading = getPageConnectionsLoading(state());
      const pageConnectionsByFieldLastLoaded = getPageConnectionsLastLoaded(state());
      const elementConnectionsByFieldLoading = getElementConnectionsLoading(state());
      const elementConnectionsByFieldLastLoaded = getElementConnectionsLastLoaded(state());
      // only request properties for those that we don't have yet or haven't requested in a bit
      const now = Date.now();
      const needsRefetch = (lastLoaded: Date | undefined) =>
        now - (lastLoaded?.getTime() ?? 0) >= MAX_AGE_MS_MINUTE;
      const reqProperties = properties.filter(prop => {
        const fieldName = unscopedVarName(prop.name, prop.type);
        switch (scope) {
          case Scope.evt:
            return (
              !eventConnectionsByFieldLoading.has(fieldName) &&
              needsRefetch(eventConnectionsByFieldLastLoaded.get(fieldName))
            );
          case Scope.page:
            return (
              !pageConnectionsByFieldLoading.has(fieldName) &&
              needsRefetch(pageConnectionsByFieldLastLoaded.get(fieldName))
            );
          case Scope.elem:
            return (
              !elementConnectionsByFieldLoading.has(fieldName) &&
              needsRefetch(elementConnectionsByFieldLastLoaded.get(fieldName))
            );
        }
        return false;
      });
      if (reqProperties.length) {
        dispatch(SearchMetaActions.markConnectionsByPropertyLoading(reqProperties, scope)); // immediately mark loading (req might get queued)
        dispatch(
          SearchMetaRequestActions.getCustomSchemaConnectionsByProperty(
            orgId,
            reqProperties,
            scope,
          ),
        ); // then fire request
      }
    },
};

export type SearchMetaActions =
  | RequestActionType<typeof SearchMetaRequestActions>
  | ActionType<typeof SearchMetaActions>;
export type SearchMetaThunkAction = AppThunkAction<void, SearchMetaActions>;
